// API helpers for the Admin Dashboard: users & roles (Cognito), page
// visibility, and usage & audit. react-query read hooks + plain async
// mutation functions, same shape as useVocab/useOntology.

import { useQuery } from '@tanstack/react-query';
import { apiFetch, authHeaders, failureFromResponse, type ApiFailure, type ApiResult } from './api';

export type Role = 'Owner' | 'Admin' | 'PowerUser' | 'User' | 'Auditor';

// ---- store: re-fold to the declared prefixes ----

export interface NamespaceFoldReport {
  scanned: number;
  rewritten: number;
  merged: number;
  by_prefix: [string, number][];
}

/** Re-fold every stored identifier to the spelling the declared prefixes give it
 *  (Admin and Owner). `dry_run` counts what would move and writes nothing. */
export async function foldNamespaces(
  body: { base?: string; dry_run: boolean },
  accessToken: string,
) {
  return apiFetch<NamespaceFoldReport>('/api/v1/admin/store/fold-namespaces', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Merge one prefix into another across the store (Admin and Owner): every
 *  `from:local` identifier becomes `into:local`, twins merge, and the merged
 *  prefix leaves the registry. `dry_run` counts what would move. */
export async function mergeNamespacePrefix(
  body: { from: string; into: string; dry_run: boolean },
  accessToken: string,
) {
  return apiFetch<NamespaceFoldReport>('/api/v1/admin/store/merge-prefix', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---- users & roles ----

export interface AdminUser {
  username: string;
  email: string | null;
  status: string;
  enabled: boolean;
  role: Role | null;
}

export function useAdminUsers(accessToken: string | undefined, enabled: boolean) {
  return useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    enabled: enabled && !!accessToken,
    queryFn: async () => {
      const r = await apiFetch<{ users: AdminUser[] }>('/api/v1/admin/users', accessToken as string);
      if (!r.ok) throw new Error(r.message);
      return r.data.users;
    },
  });
}

export async function inviteUser(email: string, role: Role, accessToken: string) {
  return apiFetch<{ email: string }>('/api/v1/admin/users', accessToken, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

export async function setUserRole(username: string, role: Role, accessToken: string) {
  return apiFetch<null>('/api/v1/admin/users/role', accessToken, {
    method: 'POST',
    body: JSON.stringify({ username, role }),
  });
}

export async function setUserEnabled(username: string, enabled: boolean, accessToken: string) {
  return apiFetch<null>('/api/v1/admin/users/enabled', accessToken, {
    method: 'POST',
    body: JSON.stringify({ username, enabled }),
  });
}

/** Access recovery, state-labeled: a FORCE_CHANGE_PASSWORD user (invited,
 *  never signed in) gets the invite re-sent with a new temporary password;
 *  a CONFIRMED user gets a forced reset (Cognito emails a reset code). */
export async function recoverUserAccess(
  username: string,
  status: string,
  accessToken: string,
) {
  const path =
    status === 'FORCE_CHANGE_PASSWORD'
      ? '/api/v1/admin/users/resend-invite'
      : '/api/v1/admin/users/reset-password';
  return apiFetch<null>(path, accessToken, {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
}

// ---- service accounts (machine credentials, Integration API) ----

export interface ServiceAccount {
  id: string;
  name: string;
  purpose: string;
  /** The role the account acts with — Integration (read-only) or a person's
   *  role, never above the creating administrator's own. */
  role: string;
  created_by: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface ServiceAccountList {
  accounts: ServiceAccount[];
  entitled: number;
  active: number;
}

export function useServiceAccounts(accessToken: string | undefined, enabled: boolean) {
  return useQuery<ServiceAccountList>({
    queryKey: ['admin', 'service-accounts'],
    enabled: enabled && !!accessToken,
    queryFn: async () => {
      const r = await apiFetch<ServiceAccountList>(
        '/api/v1/admin/service-accounts',
        accessToken as string,
      );
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

/** The response's `key` is shown exactly once — it cannot be retrieved again. */
export async function createServiceAccount(
  name: string,
  purpose: string,
  role: string,
  accessToken: string,
) {
  return apiFetch<{ account: ServiceAccount; key: string; notice: string; mcp_path: string | null }>(
    '/api/v1/admin/service-accounts',
    accessToken,
    { method: 'POST', body: JSON.stringify({ name, purpose, role }) },
  );
}

export async function revokeServiceAccount(id: string, accessToken: string) {
  return apiFetch<{ revoked: string }>(
    `/api/v1/admin/service-accounts/${encodeURIComponent(id)}`,
    accessToken,
    { method: 'DELETE' },
  );
}

/** One thing an agent test checked, in the order a call from the agent
 *  meets it. */
export interface AgentCheck {
  check: string;
  ok: boolean;
  detail: string;
}

export interface AgentTestResult {
  ok: boolean;
  question: string;
  checks: AgentCheck[];
  answer: string | null;
  elapsed_ms: number;
}

/** Ask one question as the agent, through the same path an MCP call runs. */
export async function testServiceAccount(id: string, accessToken: string, question?: string) {
  return apiFetch<AgentTestResult>(
    `/api/v1/admin/service-accounts/${encodeURIComponent(id)}/test`,
    accessToken,
    { method: 'POST', body: JSON.stringify(question ? { question } : {}) },
  );
}

// ---- page visibility ----

export interface EffectivePage {
  key: string;
  enabled: boolean;
  always_on: boolean;
  /** Roles the page is scoped to; empty = all roles. */
  allowed_roles: Role[];
  /** Works on a phone-sized viewport. Static per page; not admin-settable. */
  mobile: boolean;
}

/** The admin Pages-tab read (includes always-on flags). */
export function useAdminPages(accessToken: string | undefined, enabled: boolean) {
  return useQuery<EffectivePage[]>({
    queryKey: ['admin', 'pages'],
    enabled: enabled && !!accessToken,
    queryFn: async () => {
      const r = await apiFetch<{ pages: EffectivePage[] }>(
        '/api/v1/admin/pages',
        accessToken as string,
      );
      if (!r.ok) throw new Error(r.message);
      return r.data.pages;
    },
  });
}

export async function setPageVisibility(
  body: { page_key: string; enabled: boolean; allowed_roles: Role[] },
  accessToken: string,
) {
  return apiFetch<null>('/api/v1/admin/pages', accessToken, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// ---- branding ----

export interface AdminBranding {
  welcome_title: string | null;
  welcome_tagline: string | null;
  show_tagline: boolean;
  logo: { mime: string; bytes: number; updated_at: string } | null;
  updated_by: string | null;
}

export function useAdminBranding(accessToken: string | undefined, enabled: boolean) {
  return useQuery<AdminBranding>({
    queryKey: ['admin', 'branding'],
    enabled: enabled && !!accessToken,
    queryFn: async () => {
      const r = await apiFetch<AdminBranding>('/api/v1/admin/branding', accessToken as string);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

/** Blank title or tagline restores the product's; show_tagline false hides the line. */
export async function setWelcome(
  body: { welcome_title: string; welcome_tagline: string; show_tagline: boolean },
  accessToken: string,
) {
  return apiFetch<null>('/api/v1/admin/branding', accessToken, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** The image goes up as its own bytes — PNG or JPEG, judged server-side by
 *  content, at most 512 KB. */
export async function uploadLogo(file: File, accessToken: string): Promise<ApiResult<null>> {
  const res = await fetch('/api/v1/admin/branding/logo', {
    method: 'PUT',
    headers: { ...authHeaders(accessToken), 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (res.ok) return { ok: true, data: null };
  return failureFromResponse(res);
}

export async function deleteLogo(accessToken: string) {
  return apiFetch<null>('/api/v1/admin/branding/logo', accessToken, { method: 'DELETE' });
}

// ---- usage & audit ----

export interface AuditEntry {
  id: number;
  ts: string;
  actor: string;
  actor_email: string | null;
  /** A service account's name when the actor is one; absent for people. */
  actor_name?: string;
  actor_kind?: 'service_account';
  role: string | null;
  action: string;
  target: string | null;
  detail: string | null;
}

/** `kind: 'data'` — the Database Management transaction log (data operations
 *  only, readable by ImportData roles); `'admin'` — admin-console actions;
 *  omit for everything (ViewAuditLog only). */
export type AuditKind = 'data' | 'admin' | 'llm' | 'query' | 'knowledge';

export function useAuditLog(
  accessToken: string | undefined,
  enabled: boolean,
  kind?: AuditKind,
) {
  return useQuery<{ entries: AuditEntry[]; available: boolean }>({
    queryKey: ['admin', 'audit', kind ?? 'all'],
    enabled: enabled && !!accessToken,
    // A log is read to see what just happened: an edit made a moment ago on
    // another page is there when the log is opened or its window refocused.
    staleTime: 0,
    queryFn: async () => {
      const r = await apiFetch<{ entries: AuditEntry[]; available: boolean }>(
        kind ? `/api/v1/admin/audit?kind=${kind}` : '/api/v1/admin/audit',
        accessToken as string,
      );
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

/** Download the audit trail as CSV (admin/auditor). Returns the CSV text and
 *  the server's suggested filename; the caller turns it into a blob download. */
export async function exportAuditCsv(
  accessToken: string,
  kind?: AuditKind,
): Promise<{ ok: true; text: string; filename: string } | ApiFailure> {
  const url = kind
    ? `/api/v1/admin/audit/export?kind=${kind}`
    : '/api/v1/admin/audit/export';
  // authHeaders, not a bare Authorization header: the session header is part
  // of the auth contract, and omitting it made this export 401 on every
  // OIDC deployment — the button had never worked (2026-08-06 audit B2).
  const res = await fetch(url, { headers: authHeaders(accessToken) });
  if (!res.ok) return failureFromResponse(res);
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'tres-audit.csv';
  return { ok: true, text: await res.text(), filename };
}

export interface Usage {
  triples: number;
  graphs: number;
  backend: string;
}

export function useUsage(accessToken: string | undefined, enabled: boolean) {
  return useQuery<Usage>({
    queryKey: ['admin', 'usage'],
    enabled: enabled && !!accessToken,
    queryFn: async () => {
      const r = await apiFetch<Usage>('/api/v1/admin/usage', accessToken as string);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

// ---- user activity (logins + time on site) ----

export interface UserActivity {
  user_id: string;
  email: string | null;
  logins: number;
  first_seen: string;
  last_seen: string;
  active_seconds: number;
}

export function useUserActivity(accessToken: string | undefined, enabled: boolean) {
  return useQuery<{ entries: UserActivity[]; available: boolean }>({
    queryKey: ['admin', 'sessions'],
    enabled: enabled && !!accessToken,
    queryFn: async () => {
      const r = await apiFetch<{ entries: UserActivity[]; available: boolean }>(
        '/api/v1/admin/sessions',
        accessToken as string,
      );
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

// ---------------------------------------------------------------------------
// Customer-supplied LLM provider (BYO-LLM)
//
// NOTE the asymmetry, and keep it: settings are readable, the key is not.
// `key_configured` and `key_set_at` say THAT a key exists; nothing here can
// return one, because the backend has no endpoint that does and the secret
// store has no read operation. There is no "reveal key" affordance to add.
// ---------------------------------------------------------------------------

/// One provider's saved settings and whether it holds a key. Never the key.
export interface ProviderStatus {
  provider: string;
  model: string;
  endpoint: string | null;
  key_configured: boolean;
  key_set_at: string | null;
  /// Whether this is the provider currently in force.
  selected: boolean;
}

export interface LlmConfigStatus {
  customer_configured: boolean;
  provider: string | null;
  model: string | null;
  endpoint: string | null;
  key_configured: boolean;
  key_set_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
  /// False when this deployment cannot store a key at all.
  secret_storage_available: boolean;
  supported_providers: string[];
  /// What is serving requests right now — differs from the stored provider only
  /// when a saved configuration failed to load at startup.
  active_provider: string | null;
  active_model: string | null;
  /// Every provider a configuration was saved for. Switching provider keeps
  /// each one's key, so the page reports them per provider rather than as one
  /// flag for the instance.
  providers: ProviderStatus[];
}

export function useLlmConfig(accessToken: string | undefined, enabled: boolean) {
  return useQuery<LlmConfigStatus>({
    queryKey: ['admin', 'llm-config'],
    enabled: enabled && !!accessToken,
    queryFn: async () => {
      const r = await apiFetch<LlmConfigStatus>(
        '/api/v1/admin/llm-config',
        accessToken as string,
      );
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

/// Omit `apiKey` to keep the stored key; supplying one overwrites it.
export async function setLlmConfig(
  body: { provider: string; model: string; endpoint?: string | undefined; api_key?: string | undefined },
  accessToken: string,
) {
  return apiFetch<{ status: string; provider: string; model: string; applies: string }>(
    '/api/v1/admin/llm-config',
    accessToken,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

export interface ProviderTestResult {
  ok: boolean;
  provider: string;
  model: string;
  /** The model the provider says answered, when it reports one. */
  reported_model?: string | null;
  reply?: string;
  error?: string;
  elapsed_ms?: number;
}

/// Send one short prompt through these settings, saved or not. Nothing is
/// stored; omit `api_key` to use the key the site holds for that provider.
export async function testLlmConfig(
  body: { provider: string; model: string; endpoint?: string | undefined; api_key?: string | undefined },
  accessToken: string,
) {
  return apiFetch<ProviderTestResult>('/api/v1/admin/llm-config/test', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** A provider's models for the model menu; `models` is empty, with `reason`,
 *  when the provider gave no list. */
export interface ProviderModels {
  provider: string;
  models: string[];
  reason?: string;
}

export async function listLlmModels(
  body: { provider: string; endpoint?: string | undefined; api_key?: string | undefined },
  accessToken: string,
) {
  return apiFetch<ProviderModels>('/api/v1/admin/llm-config/models', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

const PROVIDER_NAMES: Record<string, string> = {
  ollama: 'Ollama',
  claude: 'Anthropic Claude',
  openai: 'OpenAI',
};

/** The name a person knows a provider by; the id where there is none. */
export function providerDisplayName(id: string): string {
  return PROVIDER_NAMES[id] ?? id;
}

/// Remove one provider's settings and key, or every provider when `provider`
/// is omitted.
export async function clearLlmConfig(accessToken: string, provider?: string) {
  const query = provider ? `?provider=${encodeURIComponent(provider)}` : '';
  return apiFetch<{ status: string; cleared: string; reverted_to: string }>(
    `/api/v1/admin/llm-config${query}`,
    accessToken,
    { method: 'DELETE' },
  );
}
